import React from 'react';
import { plural } from '@lingui/macro';

import { ReviewStarFill } from '@rover/icons';
import { Box, Button, Flex, Text } from '@rover/kibble/core';
import {
  getAccessibilityLabel,
  useStarColor,
} from '@rover/kibble/patterns/StarRating/StarRating.common';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';

import SitterHighlight from '../SitterHighlight/SitterHighlight';

type Props = {
  ratingsAverage: string;
  reviewsCount: number;
  onReviewsCountPress?: () => void;
};

export default function SimplifiedRatingsReview({
  ratingsAverage,
  reviewsCount,
  onReviewsCountPress,
}: Props): React.ReactElement {
  const rating = parseFloat(ratingsAverage) || 0;
  const hasRating = rating > 0;
  const starFill = useStarColor();

  const ratingText = hasRating ? rating.toFixed(1) : '-';
  const ratingsAria = getAccessibilityLabel(hasRating ? rating : null);
  const reviewsAria = plural(reviewsCount, { one: '# review', other: '# reviews' });

  if (!onReviewsCountPress) {
    const ratingsReviewText = `<0>${ratingText}</0> (${reviewsCount})`;
    return (
      <SitterHighlight
        dataTestId="ratings-reviews-highlight"
        text={ratingsReviewText}
        icon={ReviewStarFill}
        iconFill={starFill}
        ariaLabel={`${ratingsAria}, ${reviewsAria}`}
      />
    );
  }

  return (
    <Flex gap="2x" flexDirection="row" alignItems="center" data-testid="ratings-reviews-highlight">
      <ReviewStarFill height="20px" width="20px" fill={starFill} aria-hidden />
      <Box webAs="span">
        <A11yHiddenBox webAs="span">{ratingsAria}</A11yHiddenBox>
        <Box webAs="span" webProps={{ 'aria-hidden': true }}>
          <Text textColor="primary" fontWeight="semibold">
            {ratingText}
          </Text>{' '}
        </Box>
        <Button
          variant="link"
          onPress={onReviewsCountPress}
          aria-label={reviewsAria}
          sx={{
            color: 'text.primary',
            minWidth: 24,
            textDecoration: 'none',
            '& span': { fontWeight: 400 },
          }}
          testID="reviews-count-link"
        >
          (
          <Box webAs="span" sx={{ textDecoration: 'underline' }}>
            {reviewsCount}
          </Box>
          )
        </Button>
      </Box>
    </Flex>
  );
}
